import { router } from 'expo-router';
import { useSetAtom } from 'jotai';
import { memo, useCallback, useMemo } from 'react';
import {
    Icon,
    redactPrivate,
    type RowHeaderProps,
    RowList,
    TextRow,
    View,
} from 'smoothie';
import { SHORT_READABLE_DATE_FORMAT } from 'smoothie/src/utils/dateUtils';

import {
    centsToFormattedDollars,
    getTransactionIcon,
} from '../../../../../common/utils/bankingUtils';
import {
    format,
    formatWithoutTimezone,
} from '../../../../../common/utils/dateUtils';
import { selectedTransactionV2Atom } from '../../../../state/SpacesAtoms';
import {
    AMOUNT_SIGN_BY_TYPE,
    type Transaction,
} from '../../../../types/Transactions';

interface TransactionListProps {
    transactions: Transaction[];
    syncteraAccountId: string;
    headerProps?: RowHeaderProps;
}

const TransactionList = memo(
    ({
        transactions,
        syncteraAccountId,
        headerProps,
    }: TransactionListProps) => {
        const setSelectedTransaction = useSetAtom(selectedTransactionV2Atom);

        const openTransactionDetails = useCallback(
            (transaction: Transaction) => {
                setSelectedTransaction({ transaction, syncteraAccountId });
                router.navigate({
                    pathname: '/banking/transaction-details-v2',
                    params: { syncteraAccountId },
                });
            },
            [setSelectedTransaction, syncteraAccountId],
        );

        const transactionRows = useMemo(() => {
            return transactions.map((transaction: Transaction) => {
                const {
                    status,
                    dcSign,
                    amount,
                    description,
                    transactionTime,
                    postedDate,
                } = transaction;

                // Use postedDate, which should exist for posted transactions, otherwise fallback to transaction time for pending transactions
                // Posted date does not have a timestamp, make sure to ignore timezone so we don't incidentally offset the day.
                const transactionDate =
                    status === 'posted' && postedDate != null
                        ? formatWithoutTimezone(
                              postedDate,
                              SHORT_READABLE_DATE_FORMAT,
                          )
                        : format(
                              new Date(transactionTime),
                              SHORT_READABLE_DATE_FORMAT,
                          );

                const formattedAmount = `${AMOUNT_SIGN_BY_TYPE[dcSign]}${centsToFormattedDollars(amount)}`;

                return (
                    <TextRow
                        key={transaction.uuid}
                        title={description}
                        titleProps={{ ...redactPrivate() }}
                        subtitle={transactionDate}
                        secondaryText={formattedAmount}
                        secondarySubtext={
                            /pending/i.exec(status) ? 'Pending' : undefined
                        }
                        leftContentOverride={
                            // add margin to align with other elements in text row.
                            <View marginTop={6}>
                                <Icon
                                    icon={getTransactionIcon(transaction)}
                                    size={16}
                                />
                            </View>
                        }
                        onPress={() => {
                            openTransactionDetails(transaction);
                        }}
                        isPrivate={true}
                        containerProps={{
                            backgroundColor: '$gray100',
                            alignItems: 'flex-start',
                        }}
                    />
                );
            });
        }, [transactions, openTransactionDetails]);

        return (
            <RowList rowHeaderProps={headerProps}>{transactionRows}</RowList>
        );
    },
);

export default TransactionList;
